import { createContext, useContext, useSyncExternalStore } from 'react'

export interface MobileWorkspaceContextValue {
  selectTicker: (ticker: string) => void
}

export const MobileWorkspaceContext = createContext<MobileWorkspaceContextValue | null>(null)

// ─── Global selectTicker for components outside the provider tree (e.g. TopBar) ───

type Listener = () => void

let _globalSelectTicker: ((ticker: string) => void) | null = null
const _listeners = new Set<Listener>()

export function setGlobalSelectTicker(fn: ((ticker: string) => void) | null) {
  _globalSelectTicker = fn
  _listeners.forEach((l) => l())
}

export function getGlobalSelectTicker(): ((ticker: string) => void) | null {
  return _globalSelectTicker
}

export function subscribeGlobalSelectTicker(listener: Listener): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

/**
 * Hook to access the mobile workspace's selectTicker from anywhere in the tree.
 * Returns null when no MobileWorkspace is mounted.
 *
 * Both sources are read on every render: the context, and the global
 * registration through useSyncExternalStore. Returning early on the context
 * used to skip the subscription hooks, which changed the hook count whenever
 * the provider value flipped on a mounted consumer.
 */
export function useMobileWorkspace(): MobileWorkspaceContextValue | null {
  const ctx = useContext(MobileWorkspaceContext)
  // Global registration, for TopBar and other components outside the provider.
  const globalFn = useSyncExternalStore(
    subscribeGlobalSelectTicker,
    getGlobalSelectTicker,
    () => null,
  )
  if (ctx) return ctx
  return globalFn ? { selectTicker: globalFn } : null
}
